import React from "react"
import { makeStyles } from "tss-react/mui"
import { Menu, MenuItem, ListItemIcon, Divider } from "@mui/material"
import {
  Videocam as VideocamIcon,
  ChatBubbleOutlined as PersonalMsgIcon,
  QuestionAnswer as PrivateChatIcon,
  VideoCall as InviteCamIcon,
  Block as ProhibitIcon,
  AttachFile as SendFileIcon,
  Person as ProfileIcon,
  PersonAdd as FriendIcon,
  PersonAddDisabled as FriendPendingIcon,
  Check as FriendAcceptIcon,
  Clear as FriendDeclineIcon,
  PersonRemove as FriendRemoveIcon,
  VolumeOff as MuteIcon,
  VolumeUp as UnmuteIcon,
  ExitToApp as KickIcon,
  Gavel as BanIcon,
  Public as BanIpIcon,
} from "@mui/icons-material"
import { useComposer } from "./composer/ComposerContext"
import { formatRelativeTime } from "../utils/formatTime"
import { canRestrictRole } from "../utils/roleRank"

// Presence thresholds (ms) — must match the backend (socketService.js):
//   active  (green)  : last activity < 5 minutes ago
//   idle    (yellow) : 5–30 minutes ago
//   away    (red)    : >= 30 minutes ago (or unknown)
const PRESENCE_IDLE_AFTER_MS = 5 * 60 * 1000
const PRESENCE_AWAY_AFTER_MS = 30 * 60 * 1000

// macOS detection. On Mac we ALSO open the row context menu on a plain left
// click (in addition to right-click), because single-button Apple mice / users
// without right-click enabled otherwise can't reach it. Right-click still works
// everywhere. `navigator.platform` is deprecated but remains the most reliable
// Mac signal across browsers; userAgentData?.platform is the modern fallback.
export const IS_MAC = (() => {
  if (typeof navigator === "undefined") return false
  const plat =
    navigator.userAgentData?.platform || navigator.platform || ""
  return /mac/i.test(plat)
})()

// Long-press duration (ms) for opening the context menu on touch devices,
// which have no right-click. Matches the typical native long-press feel.
export const LONG_PRESS_MS = 500

// Map a server-stamped lastActiveAt to a presence state. `now` is passed in so
// callers share a single clock read per render (and so the minute ticker can
// drive transitions without new server data). Returns "active" | "idle" | "away".
export const presenceState = (lastActiveAt, now) => {
  const ts = typeof lastActiveAt === "number" ? lastActiveAt : Date.parse(lastActiveAt)
  if (!ts || Number.isNaN(ts)) return "away" // no activity info → treat as away
  const elapsed = now - ts
  if (elapsed < PRESENCE_IDLE_AFTER_MS) return "active"
  if (elapsed < PRESENCE_AWAY_AFTER_MS) return "idle"
  return "away"
}

// Build the enriched user row that both the sidebar list and this menu consume
// — one code path so the two surfaces can't drift apart. Derives:
//   isMe     — row belongs to the current user.
//   hasWebcam — real webcam state from active SFU producers (overrides any mock
//              field). My own producer is excluded from producersByUser (you
//              don't consume your own uplink), so self reads isPublishing.
//   micOnly  — still publishing, but the video track is dropped (paused) — mic
//              stays live (dropping the LAST track ends the publish, so a
//              paused video implies an active mic). Self reads its own
//              videoTrackOn; others read the watcher-side pausedVideoUsers flag.
//   presence — activity-based "active" | "idle" | "away" driving the presence
//              dot / the menu's Status row. Self is always active — I'm clearly
//              here interacting.
export const enrichUser = (
  user,
  { currentUserId, isPublishing, producersByUser = {}, pausedVideoUsers = [], videoTrackOn = true, now }
) => {
  const isMe = currentUserId != null && user.id === currentUserId
  const hasWebcam = isMe ? isPublishing : !!producersByUser[user.id]
  return {
    ...user,
    isMe,
    hasWebcam,
    micOnly: hasWebcam && (isMe ? !videoTrackOn : pausedVideoUsers.includes(user.id)),
    presence: isMe ? "active" : presenceState(user.lastActiveAt, now),
  }
}

const useStyles = makeStyles()((theme) => ({
  // A clearly-visible horizontal break in the context menu — thicker and
  // higher-contrast than MUI's default hairline Divider, with extra vertical
  // breathing room, so the misclick-safe fences around "Invite to watch my cam"
  // read as real separators rather than faint lines.
  menuBreak: {
    height: 2,
    margin: theme.spacing(0.75, 0),
    backgroundColor: "rgba(255,255,255,0.18)",
  },
  listItemIcon: {
    minWidth: 32,
  },
}))

// The shared user context menu — ONE instance lives in App and is summoned
// from both the sidebar user rows and chat-stream usernames (the items are
// user-scoped, not surface-scoped, so it reads identically from either).
// Wired items: Mention, Private Chat, Invite to Private Chat (only while I
// publish & not my row), Show/Close Cam (watch toggle), Block/Allow View
// (per-watcher block toggle, while I publish), Send File, Profile, Kick.
// Everything else is stubbed via onRowAction. Admin actions (kick/ban/ban-ip)
// show only for ADMIN/OWNER and never on my own row. `user` is the ENRICHED
// row (see enrichUser above) for whoever the menu was summoned on.
const UserContextMenu = ({
  open,
  position,
  user,
  onClose,
  currentUserRole,
  isPublishing = false,
  watchingUsers = [],
  blockedWatchers = [],
  blockedByUsers = [],
  friendIds = new Set(),
  incomingRequestIds = new Set(),
  outgoingRequestIds = new Set(),
  ignoredUserIds = new Set(),
  // Tenant feature gate — default ON (matches backend default). When false, all
  // friend menu items are hidden regardless of relationship state.
  friendsEnabled = true,
  // Guest capability gates — default ON here so a missing prop never hides a
  // control from a member. App.js resolves these per row (both sides are
  // checked: neither I nor the target may be a guest). The server enforces the
  // same rule, so hiding the item only avoids a control that always fails.
  canPrivateChatWith = true,
  canSendFileTo = true,
  onWatchToggle,
  onToggleWatchPermission,
  onInviteUser,
  onOpenVolume,
  onRowAction,
}) => {
  const { classes } = useStyles()
  // Lets the menu's "Mention" drop an @mention pill into the chat composer.
  // Context hook — the menu must render inside ComposerProvider.
  const { insertMention } = useComposer()

  const u = user
  if (!u) return null
  const isMe = u.isMe
  const isAdmin = ["ADMIN", "OWNER"].includes((currentUserRole || "").toUpperCase())
  // Staff targets (ADMIN or OWNER) are protected — kick/ban and ignore cannot be
  // applied to them. Compare uppercased to be consistent with the isAdmin check.
  const isTargetStaff = ["ADMIN", "OWNER"].includes((u.role || "").toUpperCase())
  const isWatching = watchingUsers.includes(u.id)
  const isBlocked = blockedWatchers.includes(u.id)
  // Privilege floor for the cam-block item: I may not block a watcher who
  // outranks me (staff must be able to observe what's published). The server
  // enforces this too — hiding it just avoids a control that always fails.
  const outranksMe = !isMe && !canRestrictRole(currentUserRole, u.role)
  // Friend relationship for this target (mutually exclusive states). On my own
  // row none apply — the item stays the greyed "Add Friend" placeholder.
  const isFriend = friendIds.has(u.id)
  const hasOutgoing = outgoingRequestIds.has(u.id)
  const hasIncoming = incomingRequestIds.has(u.id)
  // Ignore / Mute state for this target — flips the item label Ignore/Unignore.
  const isIgnored = ignoredUserIds.has(u.id)
  const act = (action) => () => onRowAction && onRowAction(action, u.id)
  // Run a menu item: close the menu, then invoke fn. Wrapping keeps every item's
  // onClick a one-liner and guarantees the menu always closes.
  const runMenuItem = (fn) => () => {
    if (onClose) onClose()
    if (fn) fn()
  }

  return (
    <Menu
      open={open}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={position}
      // Right-clicking again while the menu is open closes it instead of
      // surfacing the browser's native context menu. MUI's invisible backdrop
      // sits over everything while open, so this one handler (on the backdrop)
      // catches right-clicks anywhere outside the menu items. (Left-clicks
      // outside already close via onClose.)
      slotProps={{
        backdrop: {
          // MUI's Menu defaults its backdrop to invisible; passing our own
          // backdrop props replaces (not merges) that default, so we must
          // re-assert invisible or the backdrop renders as a dimming overlay.
          invisible: true,
          onContextMenu: (e) => {
            e.preventDefault()
            if (onClose) onClose()
          },
        },
      }}
    >
      {/* Header: who this menu is for */}
      <MenuItem disabled style={{ opacity: 0.9, fontWeight: 600 }}>
        {u.username}
      </MenuItem>
      <Divider />

      {/* Messaging (stubbed for now) */}
      <MenuItem onClick={runMenuItem(() => insertMention(u))}>
        <ListItemIcon className={classes.listItemIcon}><PersonalMsgIcon fontSize="small" /></ListItemIcon>
        Mention
      </MenuItem>
      {/* Private Chat opens an AIM-style 1:1 IM window (NOT the cam invite).
          Greyed (disabled) on self — shows the menu shape without being actionable.
          Hidden entirely (not greyed) when a guest gate blocks the pair: a
          permanently-dead item reads as a bug, where an absent one reads as
          "not available here". Self keeps the greyed placeholder. */}
      {(isMe || canPrivateChatWith) && (
        <MenuItem disabled={isMe} onClick={isMe ? undefined : runMenuItem(act("privateChat"))}>
          <ListItemIcon className={classes.listItemIcon}><PrivateChatIcon fontSize="small" /></ListItemIcon>
          Private Chat
        </MenuItem>
      )}

      {/* SAFE cam invite, isolated in its own break-fenced group. A misclick
          here is harmless — it only SENDS an invite the recipient must accept;
          it never opens a stream or blocks anyone. Kept deliberately away from
          the consequential "Show Cam" / "Prohibit" actions below so a misclick
          can't slip from a benign invite into one of those. The thick menuBreak
          separators (vs. a faint hairline) make that fence visible. Only
          meaningful while I'm publishing and on another user's row. */}
      {!isMe && isPublishing ? (
        <>
          <Divider className={classes.menuBreak} />
          <MenuItem onClick={runMenuItem(() => onInviteUser && onInviteUser(u.id))}>
            <ListItemIcon className={classes.listItemIcon}><InviteCamIcon fontSize="small" /></ListItemIcon>
            Invite View
          </MenuItem>
          <Divider className={classes.menuBreak} />
        </>
      ) : (
        // No cam-invite item here, but the messaging group above still needs a
        // break before the cam controls below.
        <Divider className={classes.menuBreak} />
      )}

      {/* Consequential cam controls (wired), grouped together and fenced off
          from the safe invite above. "Show Cam" immediately starts a watch
          request / opens a stream; "Prohibit viewing me" blocks + kicks. These
          are the misclick-sensitive actions — hence their own section. */}
      {u.hasWebcam && !(blockedByUsers.includes(u.id) && !isMe) && (
        <MenuItem onClick={runMenuItem(() => onWatchToggle && onWatchToggle(u.id))}>
          <ListItemIcon className={classes.listItemIcon}><VideocamIcon fontSize="small" /></ListItemIcon>
          {isWatching ? "Close Cam" : "Show Cam"}
        </MenuItem>
      )}
      {!isMe && isPublishing && (!outranksMe || isBlocked) && (
        <MenuItem onClick={runMenuItem(() => onToggleWatchPermission && onToggleWatchPermission(u.id))}>
          <ListItemIcon className={classes.listItemIcon}><ProhibitIcon fontSize="small" /></ListItemIcon>
          {isBlocked ? "Allow View" : "Block View"}
        </MenuItem>
      )}
      {/* Their volume — only meaningful while I'm consuming them (there's no
          audio to adjust otherwise). Opens the shared App-level popover at the
          menu's position; also reachable from the tile's speaker button. */}
      {!isMe && isWatching && (
        <MenuItem onClick={runMenuItem(() => onOpenVolume && onOpenVolume(u.id, position))}>
          <ListItemIcon className={classes.listItemIcon}><UnmuteIcon fontSize="small" /></ListItemIcon>
          Volume…
        </MenuItem>
      )}
      <Divider />

      {/* People actions (stubbed) */}
      {/* Send File / Add Friend / Ignore are greyed on self — shows the menu shape
          without being actionable (legacy "enable:false" pattern). */}
      {(isMe || canSendFileTo) && (
        <MenuItem disabled={isMe} onClick={isMe ? undefined : runMenuItem(act("sendFile"))}>
          <ListItemIcon className={classes.listItemIcon}><SendFileIcon fontSize="small" /></ListItemIcon>
          Send File
        </MenuItem>
      )}
      <MenuItem onClick={runMenuItem(act("profile"))}>
        <ListItemIcon className={classes.listItemIcon}><ProfileIcon fontSize="small" /></ListItemIcon>
        Profile
      </MenuItem>
      {/* Friend item(s) — four mutually-exclusive states (Phase 3). Gated by
          friendsEnabled (Phase 6 tenant flag, default ON). Greyed on self
          (legacy enable:false), where no relationship state applies so it reads
          as the "Add Friend" placeholder. An INCOMING request renders TWO items
          (Accept + Decline); every other state is a single item. */}
      {friendsEnabled && (isMe ? (
        <MenuItem disabled>
          <ListItemIcon className={classes.listItemIcon}><FriendIcon fontSize="small" /></ListItemIcon>
          Add Friend
        </MenuItem>
      ) : isFriend ? (
        <MenuItem onClick={runMenuItem(act("friendRemove"))}>
          <ListItemIcon className={classes.listItemIcon}><FriendRemoveIcon fontSize="small" /></ListItemIcon>
          Remove Friend
        </MenuItem>
      ) : hasIncoming ? (
        <>
          <MenuItem onClick={runMenuItem(act("friendAccept"))}>
            <ListItemIcon className={classes.listItemIcon}><FriendAcceptIcon fontSize="small" /></ListItemIcon>
            Accept Friend Request
          </MenuItem>
          <MenuItem onClick={runMenuItem(act("friendDecline"))}>
            <ListItemIcon className={classes.listItemIcon}><FriendDeclineIcon fontSize="small" /></ListItemIcon>
            Decline
          </MenuItem>
        </>
      ) : hasOutgoing ? (
        <MenuItem onClick={runMenuItem(act("friendCancel"))}>
          <ListItemIcon className={classes.listItemIcon}><FriendPendingIcon fontSize="small" /></ListItemIcon>
          Cancel Friend Request
        </MenuItem>
      ) : (
        <MenuItem onClick={runMenuItem(act("friendRequest"))}>
          <ListItemIcon className={classes.listItemIcon}><FriendIcon fontSize="small" /></ListItemIcon>
          Add Friend
        </MenuItem>
      ))}
      {/* Ignore / Mute (Phase 4): greyed on self; hidden entirely for staff
          targets — users can't silence moderators (Phase 2 staff-protection
          rule). Label + icon flip on the current ignore state; one toggle action
          (ignoreToggle) — App owns the authoritative state. */}
      {!isTargetStaff && (
        <MenuItem disabled={isMe} onClick={isMe ? undefined : runMenuItem(act("ignoreToggle"))}>
          <ListItemIcon className={classes.listItemIcon}>
            {isIgnored ? <UnmuteIcon fontSize="small" /> : <MuteIcon fontSize="small" />}
          </ListItemIcon>
          {isIgnored ? "Unignore" : "Ignore / Mute"}
        </MenuItem>
      )}

      {/* Read-only info rows */}
      <Divider />
      <MenuItem disabled style={{ opacity: 0.7, fontSize: 12 }}>
        {(() => {
          const rel = u.joinedAt ? formatRelativeTime(u.joinedAt) : null
          return `Chatting time: ${rel ? (rel === "Now" ? rel : `${rel}`) : "—"}`
        })()}
      </MenuItem>
      <MenuItem disabled style={{ opacity: 0.7, fontSize: 12 }}>
        {`Status: ${
          u.presence === "active" ? "Online" : u.presence === "idle" ? "Idle" : "Away"
        }`}
      </MenuItem>

      {/* Privileged admin actions — only for ADMIN/OWNER callers, never on my own
          row, and never when the TARGET is ADMIN or OWNER (staff are protected). */}
      {isAdmin && !isMe && !isTargetStaff && <Divider />}
      {isAdmin && !isMe && !isTargetStaff && (
        <MenuItem onClick={runMenuItem(act("kick"))}>
          <ListItemIcon className={classes.listItemIcon}><KickIcon fontSize="small" /></ListItemIcon>
          Kick
        </MenuItem>
      )}
      {isAdmin && !isMe && !isTargetStaff && (
        <MenuItem onClick={runMenuItem(act("ban"))}>
          <ListItemIcon className={classes.listItemIcon}><BanIcon fontSize="small" /></ListItemIcon>
          Ban User
        </MenuItem>
      )}
      {isAdmin && !isMe && !isTargetStaff && (
        <MenuItem onClick={runMenuItem(act("banIp"))}>
          <ListItemIcon className={classes.listItemIcon}><BanIpIcon fontSize="small" /></ListItemIcon>
          Ban IP
        </MenuItem>
      )}
    </Menu>
  )
}

export default UserContextMenu
